import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { ExamHistoryEntry } from "@/lib/exam-api";

/**
 * apps/web :: components/exam :: ExamScoreTrendChart
 *
 * ROADS-430 — a small inline SVG sparkline that plots the score
 * percentage for the last N attempts in chronological order
 * (oldest → newest, left → right). Shipped as plain SVG to avoid a
 * charting-library dep on the critical learner-facing path.
 *
 * Accessibility:
 *   - SVG carries `role="img"` + `aria-label` summarising the trend
 *     ("Last 5 attempts: 60%, 70%, 75%, 80%, 90%"). Screen readers
 *     therefore announce the actual data, not "image".
 *   - We also render an `<ol>` of the raw values inside an `sr-only`
 *     wrapper so SR users who'd rather hear the list can do so.
 *   - 4.5:1-ish contrast on the line by using the brand-primary token.
 *   - The pass-threshold reference line (default 80) is rendered as a
 *     dashed horizontal rule so users see "are these trending toward
 *     passing?" at a glance.
 *   - Empty / single-attempt list: render nothing (a one-point line
 *     isn't a trend); the table below covers them.
 *
 * No interactivity: the chart is read-only. Hover-tooltips would add
 * a focus-management surface that fights with the table; defer to a
 * future iteration.
 */

export interface ExamScoreTrendChartProps {
  /**
   * Attempts in EITHER order — the component sorts by submittedAtMs
   * ascending so the rightmost point is always the newest.
   */
  attempts: ExamHistoryEntry[];
  /**
   * Pass threshold (0..100) drawn as a dashed reference line. Pass
   * `null` to suppress the line. Defaults to 80 — the most common
   * Class C threshold across our seed states.
   */
  passThreshold?: number | null;
  /** Hard cap on rendered points; oldest are dropped. Default 20. */
  maxPoints?: number;
}

const DEFAULT_PASS_THRESHOLD = 80;
const DEFAULT_MAX_POINTS = 20;

const VIEW_W = 320;
const VIEW_H = 80;
const PADDING_X = 8;
const PADDING_Y = 8;
const PLOT_W = VIEW_W - PADDING_X * 2;
const PLOT_H = VIEW_H - PADDING_Y * 2;

export default function ExamScoreTrendChart({
  attempts,
  passThreshold = DEFAULT_PASS_THRESHOLD,
  maxPoints = DEFAULT_MAX_POINTS,
}: ExamScoreTrendChartProps) {
  const { t } = useTranslation();

  // Sort ASC by submittedAtMs and clamp to maxPoints (oldest dropped).
  const points = useMemo(() => {
    const sorted = [...attempts].sort(
      (a, b) => a.submittedAtMs - b.submittedAtMs,
    );
    return sorted.slice(-maxPoints);
  }, [attempts, maxPoints]);

  // Need at least 2 points for a line — render nothing otherwise.
  if (points.length < 2) return null;

  const percents = points.map((p) =>
    Math.round((p.correctCount / Math.max(1, p.questionCount)) * 100),
  );

  const dx = points.length === 1 ? 0 : PLOT_W / (points.length - 1);

  const path = percents
    .map((pct, i) => {
      const x = PADDING_X + i * dx;
      const y = PADDING_Y + ((100 - pct) / 100) * PLOT_H;
      return `${i === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(" ");

  const thresholdY =
    passThreshold == null
      ? null
      : PADDING_Y + ((100 - passThreshold) / 100) * PLOT_H;

  const ariaLabel = t("examHistory.trend.ariaLabel", {
    count: percents.length,
    values: percents.join(", "),
  });

  return (
    <figure
      data-testid="exam-history-trend"
      className="mt-md rounded-lg border border-brand-primary/10 bg-white p-md shadow-sm sm:p-lg"
    >
      <figcaption className="text-xs font-semibold uppercase tracking-wide text-neutral-700">
        {t("examHistory.trend.heading")}
      </figcaption>
      <svg
        role="img"
        aria-label={ariaLabel}
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="none"
        className="mt-sm h-20 w-full"
      >
        {thresholdY !== null && (
          <line
            data-testid="exam-history-trend-threshold"
            x1={PADDING_X}
            x2={VIEW_W - PADDING_X}
            y1={thresholdY}
            y2={thresholdY}
            strokeDasharray="4 4"
            stroke="currentColor"
            strokeOpacity={0.3}
            strokeWidth={1}
          />
        )}
        <path
          data-testid="exam-history-trend-line"
          d={path}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {percents.map((pct, i) => {
          const x = PADDING_X + i * dx;
          const y = PADDING_Y + ((100 - pct) / 100) * PLOT_H;
          const passed = points[i].passed;
          return (
            <circle
              key={points[i].attemptId}
              cx={x}
              cy={y}
              r={3}
              fill={passed ? "currentColor" : "#fff"}
              stroke="currentColor"
              strokeWidth={1.5}
              data-testid={`exam-history-trend-point-${points[i].attemptId}`}
              data-passed={passed ? "true" : "false"}
            />
          );
        })}
      </svg>
      <ol className="sr-only" data-testid="exam-history-trend-sr-list">
        {percents.map((pct, i) => (
          <li key={points[i].attemptId}>{pct}%</li>
        ))}
      </ol>
    </figure>
  );
}
